import { Fragment, ReactElement, useRef } from 'react';
import { useRouter } from 'next/router';

import { icon_chevron_right } from '@gn-audio/icons';

import { getSharedArray, getTitle } from '../../../getters';
import { useBreakpoint } from '../../../utils';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { MobileSearchMenu } from '../../Search';
import { MinorLinksCategoryItem } from '../common';
import { MenuItems } from '../enums/menu-items.enum';
import { menuComponentsMap, submenuComponentsMap } from '../headerComponentsMapper';
import { DISPLAY_VERSIONS } from '../MegaMenuConstants';
import { MajorLinksCategory, MenuItem, MinorLinksCategory, SubmenuId } from '../MegaMenuTypes';
import { useHeaderNavigation } from '../providers';
import { MobileMajorLinksCategory } from './MobileMajorLinksCategory';

export const MobileSubmenu = (): ReactElement | null => {
  const { isMobileMenuOpen, menuItems, onMenuItemClick, activeSubmenuItem, activeItemId } =
    useHeaderNavigation();
  const router = useRouter();
  const path = router.asPath;

  const majorLinksCategories: MajorLinksCategory[] = getSharedArray(
    activeSubmenuItem?.fields,
    'MajorLinksCategories'
  );

  const minorLinksCategories: MinorLinksCategory[] = getSharedArray(
    activeSubmenuItem?.fields,
    'MinorLinksCategories'
  );

  const { isBelowLg } = useBreakpoint('lg', true);

  const ref = useRef<HTMLButtonElement>(null);

  const sideBarItems = [] as MenuItem[];
  const items = menuItems.filter((menuItem) => {
    if (menuItem.name !== 'SiteSelector' && menuItem.name !== 'Search') {
      return menuItem;
    } else {
      sideBarItems.push(menuItem);
    }
    return;
  });

  const topMenuItems =
    isMobileMenuOpen && !activeSubmenuItem ? (
      <>
        {items.map((menuItem, index) => (
          <li
            onClick={() => onMenuItemClick?.(menuItem)}
            className={`box-border cursor-pointer px-5 py-2 font-semibold leading-6 text-black`}
            key={menuItem.id}
          >
            <button
              className={`flex w-full flex-1 items-center justify-between ${DISPLAY_VERSIONS.MOBILE}`}
              ref={index === 0 ? ref : null}
            >
              <span className="flex flex-1 text-left">{getTitle(menuItem)}</span>
              <JabraIcon className={`yellow ml-1 h-3`} Icon={icon_chevron_right} />
            </button>
          </li>
        ))}
        <li>
          <ul>
            {sideBarItems.map((menuItem, index) => {
              if (menuItem.name === MenuItems.Search && path.includes('search')) {
                return null;
              }
              return (
                <Fragment key={`${menuItem.id}-parent`}>
                  {menuItem.name === MenuItems.Search ? (
                    <li>
                      <MobileSearchMenu key={menuItem.id} />
                    </li>
                  ) : (
                    <li
                      onClick={() => onMenuItemClick?.(menuItem)}
                      className={`mt-3 box-border cursor-pointer bg-gray-50 px-5 py-2 font-semibold leading-6 text-black`}
                      key={menuItem.id}
                    >
                      {menuComponentsMap[menuItem.name as SubmenuId]?.(() =>
                        onMenuItemClick?.(menuItem)
                      ) || (
                        <button
                          role="button"
                          className={`flex w-full flex-1 items-center justify-between ${DISPLAY_VERSIONS.MOBILE}`}
                          ref={index === 0 ? ref : null}
                        >
                          <span className="flex flex-1 text-left">{getTitle(menuItem)}</span>
                          <JabraIcon Icon={icon_chevron_right} className={`icon yellow ml-1 h-3`} />
                        </button>
                      )}
                    </li>
                  )}
                </Fragment>
              );
            })}
          </ul>
        </li>
      </>
    ) : null;

  return isMobileMenuOpen && isBelowLg ? (
    <div className={`submenu block lg:hidden ${DISPLAY_VERSIONS.MOBILE}`} role="menu">
      <nav className="top-menu">
        <>
          {!activeSubmenuItem && !submenuComponentsMap[activeItemId] ? (
            <ul className="flex flex-col gap-3 pb-4 pt-4" role="menubar">
              {topMenuItems}
            </ul>
          ) : null}
          {activeSubmenuItem ? (
            <>
              <div className="flex-column">
                <div className="flex-1">
                  {majorLinksCategories.map((category) => (
                    <MobileMajorLinksCategory key={category.displayName} category={category} />
                  ))}
                </div>

                {minorLinksCategories.length > 0 ? (
                  <div className="flex flex-col gap-2 bg-infographicBgGrey1 p-8 pb-7 pl-4 pt-7 lg:pl-8">
                    {minorLinksCategories.map((category) => (
                      <MinorLinksCategoryItem
                        key={category.displayName}
                        category={category}
                        displayVersion={DISPLAY_VERSIONS.MOBILE}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          ) : null}
        </>
      </nav>
    </div>
  ) : null;
};
